import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { CallHandlingProfile } from '@/stores/customer-store'

interface FormValues {
  name: string
  phoneNumber: string
  description: string
  active: boolean
}

const schema = yup.object({
  name: yup
    .string()
    .required('Name ist erforderlich')
    .max(50, 'Maximal 50 Zeichen'),
  phoneNumber: yup
    .string()
    .matches(/^[+\d\s()-]+$/, 'Ungültige Telefonnummer')
    .required('Telefonnummer ist erforderlich'),
  description: yup.string().default('').max(200, 'Maximal 200 Zeichen'),
  active: yup.boolean().default(true),
})

interface ProfileDialogProps {
  isOpen: boolean
  onClose: () => void
  onSave: (profile: Partial<CallHandlingProfile>) => Promise<void>
  profile?: CallHandlingProfile | null
}

export function ProfileDialog({ isOpen, onClose, onSave, profile }: ProfileDialogProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      phoneNumber: '',
      description: '',
      active: true,
    },
  })

  const activeValue = watch('active')

  // Load profile data when editing
  useEffect(() => {
    if (profile) {
      reset({
        name: profile.name,
        phoneNumber: profile.phoneNumber || '',
        description: profile.description || '',
        active: profile.active ?? true,
      })
    } else {
      reset({
        name: '',
        phoneNumber: '',
        description: '',
        active: true,
      })
    }
  }, [profile, reset])

  const onSubmit = async (data: FormValues) => {
    try {
      const profileData: Partial<CallHandlingProfile> = {
        name: data.name,
        phoneNumber: data.phoneNumber,
        description: data.description,
        active: data.active,
      }

      if (profile?.id) {
        profileData.id = profile.id
      }

      await onSave(profileData)
      onClose()
    } catch (error) {
      console.error('Failed to save profile:', error)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {profile ? 'Profil bearbeiten' : 'Neues Profil erstellen'}
          </DialogTitle>
          <DialogDescription>
            Konfigurieren Sie ein Weiterleitungsprofil für Anrufe
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Profilname <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              {...register('name')}
              placeholder="z.B. Hauptnummer Restaurant"
              className={errors.name ? 'border-red-300' : ''}
            />
            {errors.name && (
              <p className="text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          {/* Phone Number */}
          <div className="space-y-2">
            <Label htmlFor="phoneNumber">
              Telefonnummer <span className="text-red-500">*</span>
            </Label>
            <Input
              id="phoneNumber"
              type="tel"
              {...register('phoneNumber')}
              placeholder="+49 123 456789"
              className={errors.phoneNumber ? 'border-red-300' : ''}
            />
            {errors.phoneNumber && (
              <p className="text-sm text-red-600">{errors.phoneNumber.message}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Beschreibung (optional)</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder="Notizen zu diesem Profil..."
              rows={3}
              className={errors.description ? 'border-red-300' : ''}
            />
            {errors.description && (
              <p className="text-sm text-red-600">{errors.description.message}</p>
            )}
          </div>

          {/* Active Switch */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <Label htmlFor="active">Profil aktivieren</Label>
              <p className="text-sm text-gray-500">
                Deaktivierte Profile können nicht verwendet werden
              </p>
            </div>
            <Switch
              id="active"
              checked={activeValue}
              onCheckedChange={(checked) => setValue('active', checked)}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Speichert...' : profile ? 'Aktualisieren' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
